/**
 * Small display helpers for CMS / academy data with a per-site fallback
 * (PF-176, PF-190). Pure, unit-tested in quranweb and mirrored byte-for-byte
 * into the sibling sites.
 */

/**
 * The academy's own value when it has real text, otherwise the site's
 * fallback copy. Whitespace-only text counts as missing: the admin forms can
 * store `" "` or `""`, and either would otherwise render an empty heading.
 */
export const pickText = (value: string | null | undefined, fallback: string): string => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

/**
 * A statistic counter formatted for the SITE's locale (not the browser's),
 * with Latin digits pinned so the server render and the client count-up agree.
 */
export const formatCount = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { numberingSystem: 'latn', maximumFractionDigits: 0 }).format(value)

const RTL_SCRIPT = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/

/**
 * Direction for a statistic's `prefix + number + suffix` run (PF-190).
 * Digits and `+`/`%` are not "strong" bidi characters, so `dir="auto"` has
 * nothing to decide on for «+500» and the run would inherit the Arabic page's
 * RTL and paint as «500+». The direction therefore comes from the free CMS
 * text: RTL only when the prefix or suffix contains Arabic/Hebrew script
 * (e.g. «500 ألف»), LTR otherwise.
 */
export const statisticDirection = (prefix?: string | null, suffix?: string | null): 'ltr' | 'rtl' =>
  RTL_SCRIPT.test(`${prefix ?? ''}${suffix ?? ''}`) ? 'rtl' : 'ltr'
